import {
  ChangeDetectionStrategy,
  Component,
  Input,
  booleanAttribute,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { HugeiconsIconComponent, type IconSvgObject } from '@hugeicons/angular';
import { Search01Icon } from '@hugeicons/core-free-icons';

export type MaButtonVariant = 'primary' | 'secondary' | 'outline' | 'text' | 'danger';
export type MaButtonIconCenterPosition = 'left' | 'right';

/** Material button in the app's styling; icons come from Hugeicons. */
@Component({
  selector: 'ma-button',
  standalone: true,
  imports: [MatButtonModule, HugeiconsIconComponent],
  templateUrl: './ma-button.component.html',
  styleUrl: './ma-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Material's disabled button ignores the pointer, so a click would land on this host and still fire (click).
  host: { '[style.pointer-events]': "disabled || loading ? 'none' : null" },
})
export class MaButtonComponent {
  /** Shown while `loading` when no icon input is set. */
  readonly loadingIcon = Search01Icon;

  @Input() variant: MaButtonVariant = 'primary';
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input({ transform: booleanAttribute }) loading = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() iconStart?: IconSvgObject;
  @Input() iconCenter?: IconSvgObject;
  @Input() iconCenterPosition: MaButtonIconCenterPosition = 'left';
  @Input() iconEnd?: IconSvgObject;
  @Input() size: 'xs' | 'sm' | 'md' | 'lg' = 'md';
  @Input() label?: string;
  @Input() badge?: number | string;
  /** When true, renders as mat-icon-button for simple icon triggers. */
  @Input({ transform: booleanAttribute }) iconOnly = false;

  protected get buttonClass(): string {
    const bits: string[] = ['ma-btn'];
    if (this.variant === 'primary') {
      bits.push('ma-btn--primary');
    } else if (this.variant === 'secondary') {
      bits.push('ma-btn--secondary');
    } else if (this.variant === 'danger') {
      bits.push('ma-btn--danger');
    } else if (this.variant === 'outline') {
      bits.push('ma-btn--outline');
    } else {
      bits.push('ma-btn--text');
    }
    if (this.size === 'xs') {
      bits.push('ma-btn--xs');
    } else if (this.size === 'sm') {
      bits.push('ma-btn--sm');
    } else if (this.size === 'lg') {
      bits.push('ma-btn--lg');
    }

    if (this.loading) {
      bits.push('ma-btn--loading');
    }
    return bits.join(' ');
  }

  protected get isRaised(): boolean {
    return this.variant === 'primary' || this.variant === 'secondary' || this.variant === 'danger';
  }

  protected get isStroked(): boolean {
    return this.variant === 'outline';
  }
}
